import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { adminAPI } from '../services/api';

interface WalletTransaction {
  id: number;
  transaction_type: string;
  amount: number;
  description: string;
  from_user_id?: number;
  to_user_id?: number;
  from_user_name?: string;
  to_user_name?: string;
  created_at: string;
  status: string;
}

interface User {
  id: number;
  name: string;
  email: string;
  role_name: string;
  account_balance: number;
}

const AdminWalletContent: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferData, setTransferData] = useState({
    toUserId: '',
    amount: '',
    description: ''
  });
  const [users, setUsers] = useState<User[]>([]);
  const [transferLoading, setTransferLoading] = useState(false);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchWalletData();
    fetchUsers();
  }, [currentPage, searchTerm]);

  const fetchUsers = async () => {
    try {
      const response = await adminAPI.getUsersForTransfer();
      setUsers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchWalletData = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAdminWallet();
      console.log('🔍 [FRONTEND] API Response:', response);
      console.log('🔍 [FRONTEND] Response data:', response.data);
      console.log('🔍 [FRONTEND] Balance from API:', response.data?.data?.balance);
      setWalletBalance(response.data.data.balance || 0);
      setTransactions(response.data.data.transactions || []);
      setTotalPages(response.data.data.totalPages || 1);
    } catch (error) {
      console.error('Error fetching wallet data:', error);
      setWalletBalance(0);
      setTransactions([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!transferData.toUserId || !transferData.amount || !transferData.description) {
      window.alert('Please fill in all fields');
      return;
    } 

    try {
      setTransferLoading(true);
      await adminAPI.transferToUser({
        toUserId: parseInt(transferData.toUserId),
        amount: parseFloat(transferData.amount),
        description: transferData.description
      });
      
      window.alert('Coin Transferred successfully!');
      setShowTransferModal(false);
      setTransferData({ toUserId: '', amount: '', description: '' });
      fetchWalletData(); // Refresh wallet data
    } catch (error: any) {
      console.error('Transfer error:', error);
      window.alert(error.response?.data?.message || 'Transfer failed');
    } finally {
      setTransferLoading(false);
    }
  };

  const handleGenerateCoins = async () => {
    if (!window.confirm('Generate 1 billion coins to your wallet?')) {
      return;
    }

    try {
      setGenerateLoading(true);
      const response = await adminAPI.generateCoins();
      
      window.alert(`Successfully generated 1 billion coins!`);
      fetchWalletData(); // Refresh wallet data
    } catch (error: any) {
      console.error('Generate coins error:', error);
      window.alert(error.response?.data?.message || 'Failed to generate coins');
    } finally {
      setGenerateLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="admin-wallet">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading wallet data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-content">
      <div className="admin-section wallet-section">
        <div className="section-header">
          <div className="section-title-wrapper">
            <h2>💰 Admin Coin Wallet</h2>
            <span className="section-badge wallet-badge">FINANCIAL CONTROL</span>
          </div>
          <p>Manage your account balance and transactions</p>
        </div>

        {/* Balance Card */}
        <div className="admin-cards-grid wallet-cards-grid">
          <div className="admin-card total-revenue featured-card wallet-balance-card">
            <div className="card-corner-badge badge-purple-bright">BALANCE</div>
            <div className="card-header-row">
              <div className="card-icon icon-gradient-purple-bright pulse-slow">💳</div>
            </div>
            <div className="card-content">
              <h3>Coin Wallet Balance</h3>
              <p className="card-value card-value-wallet">{ walletBalance } Coin</p>
              <div className="card-meta-row">
                <div className="card-subtitle">
                  <span className="meta-icon">💎</span> Available for transfers
                </div>
                <div className="balance-status-indicator">
                  <span className="status-dot-green"></span> ACTIVE
                </div>
              </div>
              <div style={{ marginTop: '15px', position: 'relative', zIndex: 10 }}>
                <button 
                  onClick={handleGenerateCoins}
                  disabled={generateLoading}
                  style={{
                    padding: '8px 16px',
                    fontSize: '12px',
                    backgroundColor: '#8b5cf6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: generateLoading ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    opacity: generateLoading ? 0.6 : 1,
                    position: 'relative',
                    zIndex: 10
                  }}
                >
                  {generateLoading ? '⏳ Generating...' : '✨ Generate'}
                </button>
              </div>
            </div>
          </div>
          
          <div className="admin-card wallet-stats-card">
            <div className="card-header-row">
              <div className="card-icon icon-gradient-blue">📊</div>
            </div>
            <div className="card-content">
              <h3>Total Coin Transactions</h3> 
              <p className="card-value card-value-number">{transactions?.length || 0}</p>
              <div className="card-meta-row">
                <div className="card-subtitle">
                  <span className="meta-icon">📈</span> Transaction history
                </div>
              </div>
            </div>
          </div>

          <div className="admin-card wallet-action-card" onClick={() => setShowTransferModal(true)}>
            <div className="card-header-row">
              <div className="card-icon icon-gradient-success pulse-animation">📤</div>
            </div>
            <div className="card-content">
              <h3>Send Coin</h3> 
              <p className="card-value card-value-action">Click to Transfer Coin</p>
              <div className="card-meta-row">
                <div className="card-subtitle">
                  <span className="meta-icon">💸</span> Send funds to users
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="wallet-search-container">
          <input
            type="text"
            className="wallet-search-input"
            placeholder="🔍 Search transactions by description, user, or amount..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Transactions Table */}
        <div className="wallet-transactions-container">
          <div className="wallet-transactions-header">
            <h3 className="wallet-transactions-title">
              <span className="title-icon">📜</span> Coin Transaction History
            </h3>
            <button 
              onClick={() => setShowTransferModal(true)}
              className="admin-btn-primary wallet-send-btn"
            >
              <span className="btn-icon">📤</span>
              Send Coin
            </button>
          </div>

          {!transactions || transactions.length === 0 ? (
            <div className="wallet-empty-state">
              <div className="empty-icon-wallet">📊</div>
              <h3>No Coin transactions found</h3>
              <p>Your Coin transaction history will appear here</p>
            </div>
          ) : (
            <>
              <div className="wallet-table-wrapper">
                <table className="wallet-transaction-table">
                  <thead>
                    <tr>
                      <th>TYPE</th>
                      <th>DESCRIPTION</th>
                      <th className="text-right">NUMBER OF COINS</th>
                      <th>FROM</th>
                      <th>TO</th>
                      <th>DATE</th>
                      <th className="text-center">STATUS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions?.map((transaction) => (
                      <tr key={transaction.id}>
                        <td>
                          <span className="transaction-type-badge">
                            {transaction.transaction_type.replace('_', ' ').toUpperCase()}
                          </span>
                        </td>
                        <td className="transaction-description">
                          {transaction.description}
                        </td>
                        <td className={`transaction-amount text-right ${transaction.transaction_type.includes('debit') || transaction.transaction_type.includes('sent') ? 'amount-debit' : 'amount-credit'}`}>
                          {transaction.transaction_type.includes('debit') || transaction.transaction_type.includes('sent') ? '-' : '+'}
                          {transaction.amount} Coin
                        </td>
                        <td className="transaction-user">
                          {transaction.from_user_name || '-'}
                        </td>
                        <td className="transaction-user">
                          {transaction.to_user_name || '-'}
                        </td>
                        <td className="transaction-date">
                          {formatDate(transaction.created_at)}
                        </td>
                        <td className="text-center">
                          <span className={`status-badge-wallet ${transaction.status.toLowerCase()}`}>
                            {transaction.status.toUpperCase()}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="wallet-pagination">
                  <button 
                    className="wallet-pagination-btn"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    ← Previous
                  </button>
                  <span className="wallet-pagination-info">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button 
                    className="wallet-pagination-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Transfer Modal */}
      {showTransferModal && (
        <div className="wallet-modal-overlay" onClick={() => setShowTransferModal(false)}>
          <div className="wallet-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="wallet-modal-header">
              <h2>
                <span className="modal-title-icon">💸</span>
                Send Coin
              </h2>
              <button className="wallet-modal-close" onClick={() => setShowTransferModal(false)}>
                ✕
              </button>
            </div>
            
            <form onSubmit={handleTransfer} className="wallet-transfer-form">
              <div className="wallet-form-group">
                <label>
                  <span className="label-icon">👤</span>
                  Send to:
                </label>
                <select
                  className="wallet-form-control"
                  style={{ fontSize: '12px', lineHeight: '1.2' }} 
                  value={transferData.toUserId}
                  onChange={(e) => setTransferData({...transferData, toUserId: e.target.value})}
                  required
                >
                  <option value="">Select a user</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id} style={{ fontSize: '12px' }}>
                      Manager - {user.name} ({user.email})
                    </option>
                  ))}
                </select>
              </div>

              <div className="wallet-form-group">
                <label>
                  <span className="label-icon">💰</span>
                  Amount of Coin to send:
                </label>
                <input
                  type="number"
                  className="wallet-form-control"
                  step="0.01"
                  min="0.01"
                  value={transferData.amount}
                  onChange={(e) => setTransferData({...transferData, amount: e.target.value})}
                  placeholder="Enter number of coins to send"
                  required
                />
                {transferData.amount && (
                  <div className="amount-preview">
                    You're sending: <strong>{transferData.amount} Coin</strong>
                  </div>
                )}
              </div>

              <div className="wallet-form-group">
                <label>
                  <span className="label-icon">📝</span>
                  Description:
                </label>
                <textarea
                  className="wallet-form-control wallet-textarea" 
                  value={transferData.description}
                  onChange={(e) => setTransferData({...transferData, description: e.target.value})}
                  placeholder="Enter transfer description or purpose"
                  required
                />
              </div>

              <div className="wallet-modal-footer">
                <button 
                  type="button" 
                  className="admin-btn-secondary wallet-cancel-btn"
                  onClick={() => setShowTransferModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="admin-btn-primary wallet-submit-btn"
                  disabled={transferLoading}
                >
                  <span className="btn-icon">
                    {transferLoading ? '⏳' : '✅'}
                  </span>
                  {transferLoading ? 'Sending...' : 'Send Coin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWalletContent;

